/**
 * Module Configuration API Queries
 * React Query hooks for fetching per-client module configs from ai-auth
 *
 * Gateway mapping: /auth/* → ai-auth service (module-config via Hono proxy for sanitized reads)
 */

import { useQuery } from '@tanstack/react-query';
import { ClientModuleConfig } from '@/modules/types';
import { EnabledModulesResponse } from './interfaces';
import { apiGetOrNullOn404, isAxiosErrorWithStatus } from '@/api/_shared/http';
import { moduleKeys } from './keys';
import { serverApiClient, withClientIdHeader } from '@/services/http-client';
import { moduleConfigPath } from '@/api/auth/paths';
import type { ModuleConfigurationResponse } from './moduleConfig';

/** Re-export cache keys for consumers importing from queries. */
export { moduleKeys } from './keys';

/**
 * Parse the `moduleConfiguration.config.modules` map returned by the auth
 * API into an array of `ClientModuleConfig` objects.
 *
 * @param moduleConfiguration - Raw module configuration document from the API
 * @param clientId - Client the configuration belongs to
 * @returns Parsed module config entries
 */
function parseModuleConfigResponse(
  moduleConfiguration: ModuleConfigurationResponse['moduleConfiguration'],
  clientId: string
): ClientModuleConfig[] {
  const config = moduleConfiguration?.config;
  if (!config?.modules || typeof config.modules !== 'object') return [];

  return Object.entries(config.modules).map(([moduleId, moduleConfigData]) => ({
    moduleId,
    clientId,
    config: moduleConfigData as Record<string, unknown>,
    createdAt: moduleConfiguration?.createdAt ? new Date(moduleConfiguration.createdAt) : undefined,
    updatedAt: moduleConfiguration?.updatedAt ? new Date(moduleConfiguration.updatedAt) : undefined
  }));
}

/**
 * Fetch enabled modules for a client via the Hono server proxy.
 * Sensitive fields (tokens, internal URLs) are stripped server-side
 * for relay modules so they never reach the browser.
 *
 * Uses a separate query key from useAllModuleConfigsQuery so the admin
 * page's unsanitized cache is not shared with the runtime config.
 *
 * @param clientId - Client whose enabled modules to fetch
 */
export function useEnabledModulesQuery(clientId: string | null) {
  return useQuery<EnabledModulesResponse>({
    queryKey: moduleKeys.enabledSanitized(clientId),
    queryFn: async () => {
      if (!clientId) return { modules: [] };

      try {
        const response = await serverApiClient.get<ModuleConfigurationResponse>('module-config', withClientIdHeader(clientId));
        return {
          modules: parseModuleConfigResponse(response.data?.moduleConfiguration, clientId)
        };
      } catch (error) {
        if (isAxiosErrorWithStatus(error, 404)) return { modules: [] };
        throw error;
      }
    },
    enabled: !!clientId,
    refetchOnWindowFocus: true,
    select: (data) => {
      const enabledModules = data.modules
        .filter((m) => (m.config as Record<string, unknown>).enabled !== false)
        .map((m) => {
          const { enabled: _, ...rest } = m.config as Record<string, unknown>;
          return { ...m, config: rest };
        });
      return { modules: enabledModules };
    }
  });
}

/**
 * Fetch all module configs for a client, including disabled modules.
 * Useful for displaying preserved configuration when re-enabling a module.
 *
 * @param clientId - Client whose module configs to fetch
 */
export function useAllModuleConfigsQuery(clientId: string | null) {
  return useQuery<EnabledModulesResponse>({
    queryKey: moduleKeys.allConfigs(clientId),
    queryFn: async () => {
      if (!clientId) return { modules: [] };

      const data = await apiGetOrNullOn404<ModuleConfigurationResponse>(
        moduleConfigPath(clientId)
      );
      if (!data) return { modules: [] };
      return { modules: parseModuleConfigResponse(data.moduleConfiguration, clientId) };
    },
    enabled: !!clientId,
    refetchOnWindowFocus: true
  });
}
